import { getStore } from '../store/store';
import type { MailFolder, Maybe } from 'owa-graph-schema';
import folderIdToName from 'owa-session-store/lib/utils/folderIdToName';
import {
    ARCHIVE_FOLDER_ROOT_DISTINGUISHED_ID,
    PRIMARY_FOLDER_ROOT_DISTINGUISHED_ID,
    SHARED_FOLDER_ROOT_DISTINGUISHED_ID,
} from 'owa-folders-constants';

export interface FolderTableState {
    folderTable: Map<string, MailFolder>;
}

export const folderTableSelector = {
    folderTable: (): Map<string, MailFolder> => getStore().folderTable,
};

/** Returns true if a folder is the message folder root of a primary, archive, or shared mailbox. */
export function isMessageFolderRoot(folder: MailFolder): boolean {
    return (
        folder.distinguishedFolderType === PRIMARY_FOLDER_ROOT_DISTINGUISHED_ID ||
        folder.distinguishedFolderType === ARCHIVE_FOLDER_ROOT_DISTINGUISHED_ID ||
        (folderIdToName(folder.id, true) === SHARED_FOLDER_ROOT_DISTINGUISHED_ID &&
            folder.mailboxInfo.type === 'SharedMailbox')
    );
}

/** Returns true if a folder is specified distinguished folder. */
export function isDistinguishedFolder(
    folder: MailFolder | undefined,
    distinguishedFolderId: Maybe<string> | undefined
): boolean {
    // folder is null in search scenario
    return !!folder && folder.distinguishedFolderType == distinguishedFolderId;
}

/** Returns true if a folder is a subfolder of a specified distinguished folder. */
export function isSubFolderOfDistinguishedFolder(
    folder: MailFolder | undefined,
    distinguishedFolderId: Maybe<string> | undefined,
    state: FolderTableState = {
        folderTable: getStore().folderTable,
    }
): boolean {
    // folder is null in search scenario
    const parentFolderId = !!folder && folder.parentFolderId;

    if (!parentFolderId) {
        // The parentFolderId is null for root folder of the folder trees, which means it's not a subfolder
        return false;
    }

    let parentFolder: MailFolder | undefined = state.folderTable.get(parentFolderId);
    while (parentFolder) {
        if (parentFolder.distinguishedFolderType == distinguishedFolderId) {
            return true;
        } else if (!parentFolder.parentFolderId) {
            return false;
        }
        parentFolder = state.folderTable.get(parentFolder.parentFolderId);
    }

    return false;
}

export const isUnderDistinguishedFolder: typeof isSubFolderOfDistinguishedFolder =
    isSubFolderOfDistinguishedFolder;

/** Returns true if a folder is a specified distinguished folder or is a subfolder of it.*/
export default function isFolderOrSubFolderOfDistinguishedFolder(
    folder: MailFolder | undefined,
    distinguishedFolderId: Maybe<string> | undefined,
    state: FolderTableState = { folderTable: getStore().folderTable }
): boolean {
    return (
        isDistinguishedFolder(folder, distinguishedFolderId) ||
        isUnderDistinguishedFolder(folder, distinguishedFolderId, state)
    );
}
